import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./PaymentStatusModal.module.css";
import Button from "../Button/Button";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import SuccessTick from "../SuccessTick/SuccessTick";
import PaymentService from "../../../services/payment-services";

function IconClose() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M18 6L6 18M6 6l12 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconListCheck() {
  return (
    <svg
      className={styles.stepIcon}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M20 6L9 17l-5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconListDot() {
  return (
    <svg
      className={styles.failureBullet}
      width="8"
      height="8"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
}

function IconFailedMark() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="var(--c-danger)"
        strokeWidth="2"
        fill="var(--c-danger-bg)"
      />
      <path
        d="M15 9l-6 6M9 9l6 6"
        stroke="var(--c-danger)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconErrorMark() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="var(--c-warning-border)"
        strokeWidth="2"
        fill="var(--c-warning-bg)"
      />
      <path
        d="M12 8v4M12 16h.01"
        stroke="var(--c-warning)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const MAX_RETRIES = 10;

export default function PaymentStatusModal({
  isOpen,
  onClose,
  paymentIntentId,
  paymentDetails,
  onSuccess,
  onError,
  onRefreshPaymentMethods,
}) {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const retryRef = useRef(0);

  const verifyPaymentStatus = useCallback(async () => {
    setError(null);
    setStatus("checking");

    try {
      const response = await PaymentService.verifyPaymentStatus(
        paymentIntentId
      );

      if (!response.success) {
        throw new Error(response.message || "Failed to verify payment");
      }

      const payment = response.payment;

      if (payment.status === "succeeded") {
        setStatus("success");
        setPaymentData(payment);
        onSuccess?.(payment);
        onRefreshPaymentMethods?.();
        return;
      }

      if (payment.status === "failed" || payment.status === "canceled") {
        setStatus("failed");
        setPaymentData(payment);
        onError?.(payment);
        return;
      }

      if (retryRef.current >= MAX_RETRIES) {
        setStatus("error");
        const msgKey =
          payment.status === "processing"
            ? "checkoutPage.statusModal.verifyTimeout"
            : "checkoutPage.statusModal.verifyFailed";
        setError(t(msgKey));
        onError?.(
          new Error(
            payment.status === "processing"
              ? "Payment verification timed out"
              : "Payment verification failed"
          )
        );
        return;
      }

      retryRef.current += 1;
      setStatus("checking");
      setTimeout(verifyPaymentStatus, 2000);
    } catch (err) {
      setError(err.message);
      setStatus("error");
      onError?.(err);
    }
  }, [paymentIntentId, t, onSuccess, onError, onRefreshPaymentMethods]);

  useEffect(() => {
    if (!isOpen) return;
    if (paymentDetails?.__forceStatus) return;
    if (!paymentIntentId) return;
    retryRef.current = 0;
    verifyPaymentStatus();
  }, [
    isOpen,
    paymentIntentId,
    paymentDetails?.__forceStatus,
    verifyPaymentStatus,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (paymentDetails?.__forceStatus === "success") {
      setStatus("success");
      setPaymentData({
        status: "succeeded",
        amount: Number(paymentDetails?.amount || 0),
        currency: paymentDetails?.currency || "RON",
        created: Date.now(),
      });
      return;
    }
    if (paymentDetails?.__forceStatus === "error") {
      setStatus("error");
      setError(
        paymentDetails?.__message ||
          t("checkoutPage.statusModal.forceErrorFallback")
      );
    }
  }, [
    isOpen,
    paymentDetails?.__forceStatus,
    paymentDetails?.__message,
    paymentDetails?.amount,
    paymentDetails?.currency,
    t,
  ]);

  const handleClose = () => {
    setStatus("checking");
    setPaymentData(null);
    setError(null);
    retryRef.current = 0;
    onClose();
  };

  const handleRetry = () => {
    retryRef.current = 0;
    verifyPaymentStatus();
  };

  const goToBilling = () => {
    navigate("/billing");
  };

  const localeTag = i18n.language?.toLowerCase().startsWith("ro")
    ? "ro-RO"
    : "en-US";

  const formatPaymentStatusLabel = (s) => {
    if (!s) return s;
    if (s === "succeeded") return t("checkoutPage.statusModal.statusSucceeded");
    if (s === "failed") return t("checkoutPage.statusModal.statusFailed");
    if (s === "canceled" || s === "cancelled")
      return t("checkoutPage.statusModal.statusCanceled");
    return s;
  };

  const formatCurrency = (amount, currency = "RON") => {
    const cur = currency || "RON";
    try {
      return new Intl.NumberFormat(localeTag, {
        style: "currency",
        currency: cur,
      }).format(amount);
    } catch {
      return cur === "RON" ? `${amount.toFixed(2)} RON` : String(amount);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return t("checkoutPage.statusModal.na");

    try {
      const date = new Date(timestamp);

      if (isNaN(date.getTime())) {
        return t("checkoutPage.statusModal.invalidDate");
      }

      return date.toLocaleDateString(localeTag, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return t("checkoutPage.statusModal.invalidDate");
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            {status === "checking" &&
              t("checkoutPage.statusModal.titleChecking")}
            {status === "success" && t("checkoutPage.statusModal.titleSuccess")}
            {status === "failed" && t("checkoutPage.statusModal.titleFailed")}
            {status === "error" && t("checkoutPage.statusModal.titleError")}
          </h2>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={t("checkoutPage.statusModal.closeModalAria")}
          >
            <IconClose />
          </button>
        </div>

        <div className={styles.modalBody}>
          {status === "checking" && (
            <div className={styles.statusContainer}>
              <div className={styles.statusInner}>
                <div className={styles.heroIcon}>
                  <div className={styles.heroIconFrame}>
                    <LoadingSpinner />
                  </div>
                </div>
                <p className={styles.statusMessage}>
                  {t("checkoutPage.statusModal.checkingMessage")}
                </p>
              </div>
            </div>
          )}

          {status === "success" && paymentData && (
            <div className={styles.statusContainer}>
              <div className={styles.statusInner}>
                <div className={styles.heroIcon}>
                  <div
                    className={`${styles.heroIconFrame} ${styles.heroIconFrameSuccess}`}
                  >
                    <SuccessTick size={44} color="var(--c-primary)" />
                  </div>
                </div>
                <p className={styles.statusMessage}>
                  {t("checkoutPage.statusModal.successMessage")}
                </p>

                <div className={styles.paymentDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      {t("checkoutPage.statusModal.paymentId")}
                    </span>
                    <span
                      className={`${styles.detailValue} ${styles.detailMono}`}
                    >
                      {paymentData.stripePaymentIntentId}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      {t("checkoutPage.statusModal.amount")}
                    </span>
                    <span className={styles.detailValue}>
                      {formatCurrency(paymentData.amount, paymentData.currency)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      {t("checkoutPage.statusModal.status")}
                    </span>
                    <span
                      className={`${styles.detailValue} ${styles.statusSuccess}`}
                    >
                      {formatPaymentStatusLabel(paymentData.status)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      {t("checkoutPage.statusModal.date")}
                    </span>
                    <span className={styles.detailValue}>
                      {formatDate(
                        paymentData.lastPaymentDate ||
                          paymentData.createdAt ||
                          paymentData.created
                      )}
                    </span>
                  </div>

                  {paymentDetails?.type === "domain_extension" && (
                    <>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                          {t("checkoutPage.statusModal.domain")}
                        </span>
                        <span className={styles.detailValue}>
                          {paymentDetails.domain}
                        </span>
                      </div>
                      <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>
                          {t("checkoutPage.statusModal.extension")}
                        </span>
                        <span className={styles.detailValue}>
                          {paymentDetails.years > 1
                            ? t("checkoutPage.statusModal.extensionYears", {
                                years: paymentDetails.years,
                              })
                            : t("checkoutPage.statusModal.extensionOneYear", {
                                years: paymentDetails.years,
                              })}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                <div className={styles.nextSteps}>
                  <h4 className={styles.nextStepsTitle}>
                    {t("checkoutPage.statusModal.nextStepsTitle")}
                  </h4>
                  <ul className={styles.nextStepsList}>
                    <li className={styles.stepItem}>
                      <IconListCheck />
                      <span>{t("checkoutPage.statusModal.nextEmail")}</span>
                    </li>
                    <li className={styles.stepItem}>
                      <IconListCheck />
                      <span>{t("checkoutPage.statusModal.nextService")}</span>
                    </li>
                    {paymentDetails?.type === "domain_extension" && (
                      <li className={styles.stepItem}>
                        <IconListCheck />
                        <span>{t("checkoutPage.statusModal.nextDomain")}</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {status === "failed" && paymentData && (
            <div className={styles.statusContainer}>
              <div className={styles.statusInner}>
                <div className={styles.heroIcon}>
                  <div
                    className={`${styles.heroIconFrame} ${styles.heroIconFrameFailed}`}
                  >
                    <IconFailedMark />
                  </div>
                </div>
                <p className={styles.statusMessage}>
                  {t("checkoutPage.statusModal.failedMessage")}
                </p>

                <div className={styles.paymentDetails}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      {t("checkoutPage.statusModal.status")}
                    </span>
                    <span
                      className={`${styles.detailValue} ${styles.statusFailed}`}
                    >
                      {formatPaymentStatusLabel(paymentData.status)}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>
                      {t("checkoutPage.statusModal.amount")}
                    </span>
                    <span className={styles.detailValue}>
                      {formatCurrency(paymentData.amount, paymentData.currency)}
                    </span>
                  </div>
                </div>

                <div className={styles.failureReasons}>
                  <h4 className={styles.failureTitle}>
                    {t("checkoutPage.statusModal.commonCauses")}
                  </h4>
                  <ul className={styles.failureList}>
                    <li className={styles.failureItem}>
                      <IconListDot />
                      <span>
                        {t("checkoutPage.statusModal.causeInsufficient")}
                      </span>
                    </li>
                    <li className={styles.failureItem}>
                      <IconListDot />
                      <span>{t("checkoutPage.statusModal.causeDeclined")}</span>
                    </li>
                    <li className={styles.failureItem}>
                      <IconListDot />
                      <span>{t("checkoutPage.statusModal.causeDetails")}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className={styles.statusContainer}>
              <div className={styles.statusInner}>
                <div className={styles.heroIcon}>
                  <div
                    className={`${styles.heroIconFrame} ${styles.heroIconFrameError}`}
                  >
                    <IconErrorMark />
                  </div>
                </div>
                <p className={styles.statusMessage}>
                  {t("checkoutPage.statusModal.errorMessage")}
                </p>
                {error && (
                  <div className={styles.errorMessage}>
                    <strong>
                      {t("checkoutPage.statusModal.errorDetailsLabel")}
                    </strong>{" "}
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          {status === "checking" && (
            <Button onClick={handleClose} variant="light">
              {t("checkoutPage.statusModal.cancel")}
            </Button>
          )}

          {status === "success" && (
            <>
              <Button onClick={handleClose} variant="light">
                {t("checkoutPage.statusModal.close")}
              </Button>
              <Button onClick={goToBilling}>
                {t("checkoutPage.statusModal.goToBilling")}
              </Button>
            </>
          )}

          {status === "failed" && (
            <>
              <Button onClick={handleClose} variant="light">
                {t("checkoutPage.statusModal.close")}
              </Button>
              <Button onClick={goToBilling}>
                {t("checkoutPage.statusModal.backToBilling")}
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <Button onClick={handleRetry} variant="light">
                {t("checkoutPage.statusModal.retry")}
              </Button>
              <Button onClick={handleClose}>
                {t("checkoutPage.statusModal.close")}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
